import Button from 'tio-reimagined/components/button';
import { dropTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import EmailLookupWrapper from 'tio-employee/components/auth/email-lookup-wrapper';
import ErrorModal from 'tio-employee/components/auth/reset-password/error-modal';
import { Form, type FormResultData } from 'tio-ui/components/forms';
import resetPasswordFormSchema from 'tio-common/validation-schema/forms/reset-password-form';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type PreRegisterService from '../../services/pre-register.ts';
import type ResetPasswordController from 'tio-employee/controllers/reset-password';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'ember-data/store';

interface ResetPasswordRouteComponentSignature {
  Args: {
    controller: ResetPasswordController;
    model?: { error: string };
  };
}

interface PasswordFormData {
  password: string;
  passwordConfirmation: string;
}

export default class ResetPasswordRoute extends Component<ResetPasswordRouteComponentSignature> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare preRegister: PreRegisterService;
  @service declare notifications: NotificationsService;

  @tracked formData: PasswordFormData = {
    password: '',
    passwordConfirmation: '',
  };

  get isTokenInvalid() {
    return !!this.args.model?.error;
  }

  get passwordMinLength() {
    return this.preRegister.settings?.security?.passwordMinLength || 8;
  }

  get schema() {
    return resetPasswordFormSchema(this.passwordMinLength);
  }

  handleFormChange = (result: FormResultData<PasswordFormData>) => {
    this.formData = result.data;
  };

  resetPassword = dropTask(async () => {
    try {
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      await this.store.adapterFor('login').resetPassword({
        password: this.formData.password,
        passwordConfirmation: this.formData.passwordConfirmation,
        token: this.args.controller.token,
      });
      this.router.transitionTo('reset-password.confirmation');
    } catch (e) {
      const errorMessage = e?.errors?.length ? e.errors[0] : 'An Error Occurred';
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    }
  });

  handleSubmit = () => {
    this.resetPassword.perform();
  };

  onSubmitModal = () => {
    this.router.transitionTo('login');
  };

  <template>
    <div class="flex flex-col h-screen container mx-auto w-full overflow-auto">
      <EmailLookupWrapper @route="reset-password">
        <h1 class="text-xl my-4 text-center">{{t "login.reset_password_title"}}</h1>
        <p class="mb-4 text-center">{{t "login.reset_password_body"}}</p>
        <Form
          @data={{this.formData}}
          @schema={{this.schema}}
          @onChange={{this.handleFormChange}}
          @onSubmit={{this.handleSubmit}}
          data-test-reset-password-form
          as |form|
        >
          <div class="mb-10">
            <form.Field @name="password" as |field|>
              <field.Input
                @label={{t "login.new_password"}}
                @description={{t "login.minimum_characters" min=this.passwordMinLength}}
                @type="password"
                data-test-password-field
              />
            </form.Field>
          </div>
          <div class="mb-10">
            <form.Field @name="passwordConfirmation" as |field|>
              <field.Input
                @label={{t "login.confirm_new_password"}}
                @description={{t "login.minimum_characters" min=this.passwordMinLength}}
                @type="password"
                data-test-password-confirmation-field
              />
            </form.Field>
          </div>
          <div class="mb-6 text-center">
            <Button
              @isRunning={{this.resetPassword.isRunning}}
              @intent="primary"
              type="submit"
              class="w-36"
              data-test-submit-button
            >
              {{t "common.submit"}}
            </Button>
          </div>
          <div data-test-login-here class="mb-6 text-md text-center">
            {{t "login.need_to_register"}}
            <a class="underline text-ocean-600" href="/register">
              {{t "login.register_new_account"}}
            </a>
          </div>
        </Form>
        <ErrorModal @isOpen={{this.isTokenInvalid}} @onSubmit={{this.onSubmitModal}} />
      </EmailLookupWrapper>
    </div>
  </template>
}
